import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';

import { BeehiivPublicationIds, getPublicationTitleById } from '@blockworks/platform/api/beehiiv';
import { Article } from '@blockworks/platform/api/strapi';
import { strapiServerApi } from '@blockworks/platform/api/strapi/client/server';
import { useAuth } from '@blockworks/session/context';
import { Bit } from '@blockworks/ui/bit';
import { ContentLayout, FlexBox, Grid, Layout, Prose, Tag, Text } from '@blockworks/ui/components';

import { ArticleFeaturedSection } from '@/components/article/article-featured-section';
import ArticleSharing from '@/components/article/article-sharing';
import NewsletterSignupSection from '@/components/article/newsletter-signup-section';
import RelatedAssets from '@/components/article/related-assets';
import SignInSection from '@/components/auth/sign-in-section';
import Byline from '@/components/byline/byline';
import TopBar from '@/layout/global/top-bar/top-bar';
import { LayoutHead } from '@/layout/layout-head';
import { prepareArticleMetadata } from '@/utils/metadata/prepare-article-metadata';
import { socialSharingLinks } from '@/utils/url';

export const getStaticPaths: GetStaticPaths = async () => {
    const { data: articles = [] } = await strapiServerApi.get.articles({
        filters: { isFree: { $eq: true } },
        pagination: { pageSize: 100 },
    });

    return {
        paths: articles.map(({ slug }) => ({
            params: {
                slug,
            },
        })),
        fallback: true,
    };
};

export const getStaticProps: GetStaticProps = async ctx => {
    if (!ctx.params?.slug) {
        return { notFound: true };
    }
    try {
        const { data: [article] = [] } = await strapiServerApi.get.articles({
            filters: { slug: { $eq: ctx.params?.slug } },
        });
        if (!article) {
            return {
                notFound: true,
            };
        }

        return {
            props: {
                article,
                meta: prepareArticleMetadata(article),
            },
            revalidate: 1800,
        };
    } catch (e) {
        return {
            notFound: true,
            revalidate: 600,
        };
    }
};

const ArticleTopSection = ({ article }: { article: Article | null }) => {
    return (
        <FlexBox col my={5} gap="xl" justifyContent="stretch">
            <Prose.Section name="top" />
            <NewsletterSignupSection
                publicationTitle={getPublicationTitleById(BeehiivPublicationIds.ZeroXResearch)}
                campaign={article?.slug ?? ''}
                source="unlocked-report"
                publication={BeehiivPublicationIds.ZeroXResearch}
            />
        </FlexBox>
    );
};
const FreeResearchPage = ({ article }: { article: Article | null }) => {
    const { status } = useAuth();
    const router = useRouter();

    const topHeading = 'Key Takeaways';
    const topSection = article?.keyTakeaways;
    const articleSharingLinks = socialSharingLinks(router.asPath);

    if (!article?.isFree) {
        return <SignInSection full subtitle="Sign up for Blockworks Research to access this report." />;
    }

    return (
        <Prose.Root
            key={`${router.query.slug}-content`}
            id="content"
            content={{
                top: topSection ? `## ${topHeading}\n${topSection}` : '',
                content: article.content!,
            }}
        >
            <ContentLayout>
                <ContentLayout.Container custom>
                    <Bit.div m={5}>
                        <Grid py={6} contentType="prose-lg" gap={4} justifyContent="center" mx="auto">
                            <Grid.Col colSpan={{ initial: 12, lg: 8 }}>
                                <ArticleFeaturedSection
                                    featuredImage={article?.featuredImage}
                                    isFree={!!article?.isFree}
                                    sponsors={article?.sponsors}
                                />
                                <Text size="display-xs" weight="bold" my={4} font="display">
                                    {article?.title}
                                </Text>
                                <FlexBox wrap gap="sm" mb={4}>
                                    {article?.tags?.map(tag => (
                                        <Tag
                                            key={`${article?.id}-${tag.slug}`}
                                            href={`/research?${tag.isAsset ? 'assets' : 'tags'}=${tag.slug}`}
                                        >
                                            {tag.name}
                                        </Tag>
                                    ))}
                                </FlexBox>
                                <Byline authors={article?.authors!} publishedAt={article?.publishedAt!} />
                                {topSection && <ArticleTopSection article={article} />}
                                <Prose.Section name="content" />
                            </Grid.Col>
                            <Grid.Col colSpan={{ initial: 12, lg: 4 }} display="flex" flexDirection="column" gap={4}>
                                <RelatedAssets tags={article?.tags} />
                                <ArticleSharing articleSharingLinks={articleSharingLinks} />
                            </Grid.Col>
                        </Grid>
                    </Bit.div>
                    {status === 'unauthenticated' && <SignInSection />}
                </ContentLayout.Container>
            </ContentLayout>
        </Prose.Root>
    );
};
FreeResearchPage.getLayout = (page: React.ReactElement, metaProps: any) => (
    <Layout grid>
        <LayoutHead metaProps={metaProps} />
        <Layout.Brand />
        <Layout.Topbar>
            <TopBar />
        </Layout.Topbar>
        <Layout.ContentWithoutSidebar>{page}</Layout.ContentWithoutSidebar>
    </Layout>
);
export default FreeResearchPage;
